﻿import { useNavigate } from 'react-router';
import { useCancelCurrentPlanProjectionDetailsQuery, useCurrentPlanProjectionDetailsMutation } from '../../services';
import { CurrentPlanProjectionDetailsData } from '@xplan-pension-switching/common';
import { getSuffixFromGrowthRateBasisType } from '../../helpers/GetSuffixFromGrowthRateBasisType/GetSuffixFromGrowthRateBasisType';
import { useContext } from 'react';
import { FormHandlingContext } from '../../context/FormHandlingContext';
import { isNullOrUndefinedOrEmpty } from '../../helpers';

const getGrowthRates = (
    growthRates: {
        [key: string]: string | number | undefined;
        growthRateBasis: string;
    },
    suffix: string
) => {
    return {
        growthRateBasis: growthRates.growthRateBasis,
        growthRateLow: growthRates[`growthRateLow${suffix}`],
        growthRateMid: growthRates[`growthRateMid${suffix}`],
        growthRateHigh: growthRates[`growthRateHigh${suffix}`],
    } as CurrentPlanProjectionDetailsData['projectedGrowthRates'];
};

export const useCurrentPlanProjectionDetailsSubmitHandler = (
    entityId: number,
    scenarioId: number,
    projectionId: number
): { submitHandler: (values: CurrentPlanProjectionDetailsData) => Promise<void> } => {
    const navigate = useNavigate();
    const mutation = useCurrentPlanProjectionDetailsMutation();
    const cancelQuery = useCancelCurrentPlanProjectionDetailsQuery(entityId, scenarioId, projectionId);
    const { formReference } = useContext(FormHandlingContext);

    const submitHandler = async (values: CurrentPlanProjectionDetailsData) => {
        const suffix = getSuffixFromGrowthRateBasisType(values.projectedGrowthRates.growthRateBasis);
        const toUpdate = {
            details: {
                ...values,
                projectedGrowthRates: {
                    ...getGrowthRates(values.projectedGrowthRates, suffix),
                },
            },
        };

        const navigateToNextScreen = () =>
            navigate({
                pathname: `/current-plan-projections`,
                search: `?entityid=${entityId}&scenarioId=${scenarioId}`,
            });

        const formIsDirty = !isNullOrUndefinedOrEmpty(formReference.current?.api.formState.dirtyFields);

        if (formIsDirty) {
            await mutation.mutateAsync(
                { entityId, scenarioId, projectionId, ...toUpdate },
                {
                    onSuccess: () => {
                        cancelQuery();
                        navigateToNextScreen();
                    },
                }
            );
        } else {
            navigateToNextScreen();
        }
    };
    return {
        submitHandler,
    };
};
